import {
  IPolicyRuleCondition,
  IPolicyRuleConditionItem,
  IPolicySettingsField,
} from '@entities/policy';

import { DigitParam, RuleParam } from './policyRuleTypes';

export const serializeParams = (items: RuleParam[]): IPolicyRuleCondition => {
  return items.reduce<IPolicyRuleCondition>((acc, item) => {
    if (item.type === 'multi-select') {
      acc[item.techname] = item.selected.map(option => option.value);
      return acc;
    }
    const numericValue = item.value === '' ? undefined : Number(item.value);
    if (numericValue === undefined || Number.isNaN(numericValue)) return acc;
    const operatorMap: Record<
      DigitParam['operator'],
      keyof Exclude<IPolicyRuleCondition[string], string[]>
    > = {
      '=': 'eq',
      '>': 'gt',
      '>=': 'gte',
      '<': 'lt',
      '<=': 'lte',
    };
    const key = operatorMap[item.operator];
    acc[item.techname] = { [key]: numericValue };
    return acc;
  }, {});
};

export const buildParamsFromRule = (
  source: IPolicyRuleCondition | IPolicyRuleConditionItem[] | undefined,
  fields: IPolicySettingsField[],
): RuleParam[] => {
  if (!source) return [];

  const entries: Array<[string, IPolicyRuleCondition[string]]> = Array.isArray(source)
    ? source.map(item => [item.name, item.rawValue] as [string, IPolicyRuleCondition[string]])
    : Object.entries(source);

  return entries.map(([techname, value]) => {
    const field = fields.find(item => item.techname === techname);
    const label = field?.label ?? techname;
    if (Array.isArray(value)) {
      const valueMap = field?.values ?? {};
      const selected = value.map(item => ({
        value: item,
        label: valueMap[item] ?? item,
      }));
      return {
        techname,
        label,
        type: 'multi-select',
        values: valueMap,
        selected,
      };
    }

    const [opKey, numValue] = Object.entries(value)[0] ?? [];
    const operatorMap: Record<string, DigitParam['operator']> = {
      eq: '=',
      gt: '>',
      gte: '>=',
      lt: '<',
      lte: '<=',
    };
    return {
      techname,
      label,
      type: 'digit-compare',
      operator: operatorMap[opKey] ?? '=',
      value: numValue !== undefined ? String(numValue) : '',
    };
  });
};
